import {
  AlertFillIcon,
  ArrowRightIcon,
  BookIcon,
  CommentIcon,
  PencilIcon,
  PauseIcon,
  PlayIcon,
} from '@primer/octicons-react'
import {ActionList, Label, Spinner} from '@primer/react'

import styles from './TriageAgentOverlay.module.css'
import {useTriageAgentStatus, useUpdateTriageAgentStatus} from './use-triage-agent-status'
import {
  CREATE_TRIAGE_AGENT_CONFIGURATION_TEXT,
  CONFIG_FILE_NOT_FOUND_TEXT,
  CONFIG_FILE_FOUND_TEXT,
  CONFIG_FILE_INVALID_TEXT,
  PAUSE_AGENT_TEXT,
  RESUME_AGENT_TEXT,
  TRIAGE_AGENT_DOCS_URL,
  TRIAGE_AGENT_FEEDBACK_URL,
  TRIAGE_AGENT_PERMISSION_MESSAGE,
  TRIAGE_AGENT_TITLE,
  VIEW_DOCUMENTATION_TEXT,
  GIVE_FEEDBACK_TEXT,
} from './utils/constants'
import {getTriageAgentConfigUrl, getNewTriageAgentConfigUrl} from './utils/triage-agent-urls'

type TriageAgentOverlayContentProps = {
  repoOwner: string
  repoName: string
  onClose: () => void
  onOpenConfigModal: () => void
  viewerCanConfigureTriageAgent: boolean
  cannotConfigureReason?: string | null
  configEditorEnabled: boolean
}

export function TriageAgentOverlayContent({
  repoOwner,
  repoName,
  onClose,
  onOpenConfigModal,
  viewerCanConfigureTriageAgent,
  cannotConfigureReason,
  configEditorEnabled,
}: TriageAgentOverlayContentProps) {
  const {data, isLoading, isError: isFetchError} = useTriageAgentStatus(repoOwner, repoName)
  const {updateStatus, isPending: isUpdating} = useUpdateTriageAgentStatus(repoOwner, repoName)

  if (isLoading) {
    return (
      <div className={styles.overlayContainer}>
        <div className={styles.loadingContainer}>
          <Spinner size="medium" />
        </div>
      </div>
    )
  }

  const isError = isFetchError || !data
  if (isError) {
    return (
      <div className={styles.overlayContainer}>
        <div className={styles.header}>
          <span className={styles.title}>{TRIAGE_AGENT_TITLE}</span>
          <Label variant="success">Preview</Label>
        </div>
        <div className={styles.errorMessage}>Failed to load triage agent settings.</div>
      </div>
    )
  }

  const configUrl = getTriageAgentConfigUrl({
    repoOwner,
    repoName,
    defaultBranch: data.default_branch,
    configPath: data.config_path,
  })
  const newConfigUrl = getNewTriageAgentConfigUrl({
    repoOwner,
    repoName,
    defaultBranch: data.default_branch,
  })

  const configExists = data.config_exists
  const hasInvalidConfig = data.config_valid === false
  const isEnabled = data.enabled

  const permissionMessage = cannotConfigureReason ?? TRIAGE_AGENT_PERMISSION_MESSAGE

  const handlePauseResume = () => {
    if (configEditorEnabled && !isEnabled && !configExists) {
      // First-time enable: open config modal instead of toggling status
      onOpenConfigModal()
      return
    }
    updateStatus(!isEnabled)
  }

  const handleConfigureClick = () => {
    onOpenConfigModal()
  }

  function configLeadingIcon() {
    return <PencilIcon />
  }

  function configDescription() {
    if (hasInvalidConfig)
      return (
        <span className={styles.invalidConfig}>
          <AlertFillIcon size={12} />
          {CONFIG_FILE_INVALID_TEXT}
        </span>
      )
    if (configExists) return CONFIG_FILE_FOUND_TEXT
    return CONFIG_FILE_NOT_FOUND_TEXT
  }

  return (
    <div className={styles.overlayContainer} data-testid="triage-agent-overlay">
      <div className={styles.header}>
        <span className={styles.title}>{TRIAGE_AGENT_TITLE}</span>
        <Label variant="success">Preview</Label>
      </div>

      <ActionList>
        {/* Start/Stop Agent Option - hidden when config is invalid */}
        {!hasInvalidConfig && (
          <ActionList.Item
            onSelect={handlePauseResume}
            disabled={
              isUpdating || !viewerCanConfigureTriageAgent || (!configEditorEnabled && !configExists && !isEnabled)
            }
          >
            <ActionList.LeadingVisual>{isEnabled ? <PauseIcon /> : <PlayIcon />}</ActionList.LeadingVisual>
            {isEnabled ? PAUSE_AGENT_TEXT : RESUME_AGENT_TEXT}
            {!viewerCanConfigureTriageAgent && (
              <ActionList.Description variant="block">{permissionMessage}</ActionList.Description>
            )}
            {!configEditorEnabled && !configExists && !isEnabled && viewerCanConfigureTriageAgent && (
              <ActionList.Description variant="block">{CONFIG_FILE_NOT_FOUND_TEXT}</ActionList.Description>
            )}
          </ActionList.Item>
        )}

        {/* Configure Agent Option */}
        {!viewerCanConfigureTriageAgent ? (
          <ActionList.Item disabled data-testid="customize-agent-item">
            <ActionList.LeadingVisual>
              <PencilIcon />
            </ActionList.LeadingVisual>
            {CREATE_TRIAGE_AGENT_CONFIGURATION_TEXT}
            <ActionList.Description variant="block">{permissionMessage}</ActionList.Description>
          </ActionList.Item>
        ) : configEditorEnabled ? (
          <ActionList.Item onSelect={handleConfigureClick} data-testid="customize-agent-item">
            <ActionList.LeadingVisual>{configLeadingIcon()}</ActionList.LeadingVisual>
            {CREATE_TRIAGE_AGENT_CONFIGURATION_TEXT}
            <ActionList.Description variant="block">{configDescription()}</ActionList.Description>
          </ActionList.Item>
        ) : (
          <ActionList.LinkItem href={configExists ? configUrl : newConfigUrl} onClick={() => onClose()}>
            <ActionList.LeadingVisual>{configLeadingIcon()}</ActionList.LeadingVisual>
            {CREATE_TRIAGE_AGENT_CONFIGURATION_TEXT}
            <ActionList.Description variant="block">{configDescription()}</ActionList.Description>
            <ActionList.TrailingVisual>
              <ArrowRightIcon />
            </ActionList.TrailingVisual>
          </ActionList.LinkItem>
        )}

        <ActionList.Divider />

        <ActionList.LinkItem href={TRIAGE_AGENT_DOCS_URL} target="_blank" rel="noopener noreferrer">
          <ActionList.LeadingVisual>
            <BookIcon />
          </ActionList.LeadingVisual>
          {VIEW_DOCUMENTATION_TEXT}
        </ActionList.LinkItem>

        <ActionList.LinkItem href={TRIAGE_AGENT_FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
          <ActionList.LeadingVisual>
            <CommentIcon />
          </ActionList.LeadingVisual>
          {GIVE_FEEDBACK_TEXT}
        </ActionList.LinkItem>
      </ActionList>
    </div>
  )
}
